/**
 * OntologyGraph — Sigma.js renderer for the PDO.
 *
 * Wraps SigmaContainer + graphology and builds the graph from OntologyGraph data.
 * Handles category colors, edge type styling, click selection, and diff decorations.
 */
import { useEffect, useRef, useCallback } from 'react';
import { SigmaContainer, useLoadGraph, useSigma, useCamera } from '@react-sigma/core';
import '@react-sigma/core/lib/style.css';
import { MultiDirectedGraph } from 'graphology';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import type { OntologyGraph, OntologyNodeData, OntologyEdgeData } from '@/services/api';
import { useOntologyStore } from '@/stores/ontology';

// Entity type → node color
export const ENTITY_TYPE_COLORS: Record<string, string> = {
  ATTRIBUTE: '#2dd4bf',
  NEED:      '#a78bfa',
  VALUE:     '#fbbf24',
  TRUST:     '#34d399',
  PRODUCT:   '#fb923c',
  CATEGORY:  '#60a5fa',
  BEHAVIOR:  '#f472b6',
  BARRIER:   '#f87171',
  OTHER:     '#94a3b8',
};

// Rel type → edge color
export const REL_TYPE_COLORS: Record<string, string> = {
  DRIVES:            '#3b82f6',
  INHIBITS:          '#ef4444',
  RESOLVES_CONCERN:  '#22c55e',
  REMOVES_BARRIER:   '#f97316',
  CONFIRMS_IDENTITY: '#94a3b8',
  SATISFIES:         '#a78bfa',
  CO_OCCURS:         '#475569',
  IS_A:              '#64748b',
};

interface Props {
  graph: OntologyGraph;
  addedNodeIds?: Set<string>;
  removedNodeIds?: Set<string>;
  onNodeClick?: (nodeId: string) => void;
}

const SIGMA_SETTINGS = {
  allowInvalidContainer: true,
  renderLabels: true,
  renderEdgeLabels: false,
  labelFont: 'Inter, system-ui, sans-serif',
  labelSize: 11,
  labelWeight: '500',
  labelColor: { color: '#e2e8f0' },
  labelDensity: 0.7,
  labelGridCellSize: 100,
  labelRenderedSizeThreshold: 5,
  stagePadding: 40,
  defaultNodeType: 'circle',
  defaultEdgeType: 'line',
  zIndex: true,
  minCameraRatio: 0.02,
  maxCameraRatio: 20,
  autoRescale: true,
  autoCenter: true,
} as const;

// Inner component: has access to sigma context
function GraphLoader({
  graph: graphData,
  addedNodeIds,
  removedNodeIds,
  onNodeClick,
}: Props) {
  const loadGraph = useLoadGraph();
  const sigma = useSigma();
  const { gotoNode } = useCamera();
  const focusedNodeId = useOntologyStore((s) => s.focusedNodeId);

  const buildGraph = useCallback(() => {
    const g = new MultiDirectedGraph();

    for (const node of graphData.nodes) {
      let color = ENTITY_TYPE_COLORS[node.entity_type] ?? '#94a3b8';
      let size = 6;

      if (addedNodeIds?.has(node.id)) {
        color = '#22c55e';   // vivid green for newly added
        size = 9;
      } else if (removedNodeIds?.has(node.id)) {
        color = '#ef4444';   // red for removed
        size = 7;
      }

      if (!g.hasNode(node.id)) {
        g.addNode(node.id, {
          label: node.name,
          size,
          color,
          entity_type: node.entity_type,
          category: node.category,
          x: Math.random(),
          y: Math.random(),
        });
      }
    }

    const addedEdgeIds = new Set<string>();
    for (const edge of graphData.edges) {
      if (!g.hasNode(edge.source) || !g.hasNode(edge.target)) continue;
      const edgeId = `${edge.source}__${edge.rel_type}__${edge.target}`;
      // De-duplicate: same source/rel_type/target from multiple YAML files
      if (addedEdgeIds.has(edgeId)) continue;
      addedEdgeIds.add(edgeId);
      const color = REL_TYPE_COLORS[edge.rel_type] ?? '#334155';
      try {
        g.addDirectedEdgeWithKey(edgeId, edge.source, edge.target, {
          size: Math.max(2, Math.min(6, (edge.weight ?? 1) * 5)),
          color,
          rel_type: edge.rel_type,
          label: edge.label,
          weight: edge.weight,
        });
      } catch {
        // Skip duplicate edges silently
      }
    }

    // Run ForceAtlas2 to get initial layout positions
    forceAtlas2.assign(g, {
      iterations: 150,
      settings: {
        gravity: 1,
        scalingRatio: 10,
        slowDown: 10,
      },
    });

    return g;
  }, [graphData, addedNodeIds, removedNodeIds]);

  useEffect(() => {
    const g = buildGraph();
    loadGraph(g);
  }, [buildGraph, loadGraph]);

  // Click handling
  useEffect(() => {
    if (!sigma) return;
    const handler = ({ node }: { node: string }) => {
      onNodeClick?.(node);
    };
    sigma.on('clickNode', handler);
    return () => { sigma.off('clickNode', handler); };
  }, [sigma, onNodeClick]);

  // Pan to focused node
  useEffect(() => {
    if (!focusedNodeId || !sigma) return;
    const g = sigma.getGraph();
    if (!g.hasNode(focusedNodeId)) return;
    gotoNode(focusedNodeId);
    sigma.refresh();
  }, [focusedNodeId, sigma, gotoNode]);

  return null;
}

const SIGMA_STYLE = {
  '--sigma-background-color': '#0f172a',
} as React.CSSProperties;

export function OntologyGraph(props: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className="absolute inset-0" style={SIGMA_STYLE}>
      <SigmaContainer
        graph={MultiDirectedGraph}
        settings={SIGMA_SETTINGS}
        style={{ position: 'absolute', inset: 0 }}
      >
        <GraphLoader {...props} />
      </SigmaContainer>
    </div>
  );
}
